/**
 * German (Germany) widget strings. Third shipped table after es_ES and nl_NL
 * (#1262); same shape, same gate, same resolution ladder. `de_AT`, `de_CH`
 * and `de_DE_formal` visitors land here through the same-language fallback.
 *
 * PROVENANCE (toolkit parity, searchbar-pro 3.288.0):
 *   - Strings whose English matches a toolkit msgid are taken verbatim from
 *     the toolkit's own German table on origin/main
 *     (`includes/SearchbarPro/Translations.php` ROWS; the toolkit's CoreI18n
 *     table ships no German); where only the placeholder syntax differs
 *     ({night} → {{n}}, "Loading…" → "Wird geladen...") the wording is the
 *     toolkit's with the widget's tokens. Those keys:
 *       specs.{bedrooms,bathrooms,guests}; property-card/explorer
 *       feature_{guest,guests,bedroom,bedrooms}, view_property,
 *       price_night_*, price_excl_taxes_fees, grid_loading, slider_previous,
 *       slider_next;
 *       calendar.{tip_select_stay, tip_night_*, footer_clear_dates,
 *       min_stay_unit_*};
 *       booking.{button_close, label_check_in*, label_check_out*,
 *       placeholder_add_date, label_guests, label_adults, label_children,
 *       label_infants, label_pets, hint_*_age, aria_guest_selection,
 *       aria_decrease_*, aria_increase_*, unit_*, calendar_modal_title,
 *       label_when, button_clear_dates};
 *       checkout.{button_back, label_city, label_check_in, label_check_out,
 *       label_guests, aria_close_consent_dialog, button_go_back};
 *       confirmation.{aria_modal_close, label_row_checkin, label_row_checkout,
 *       label_row_guests}.
 *   - EVERY OTHER STRING WAS WRITTEN HERE on 2026-10-18 (lane sbp-catchup)
 *     against the en_US schema defaults and has NOT been reviewed by a native
 *     speaker. Register: formal "Sie" wherever a pronoun is unavoidable (the
 *     register Booking.com and most German operators use); the toolkit rows
 *     are impersonal and carry no pronoun.
 *
 * REVIEW (choices a native reviewer may want to change):
 *   - property-card.feature_bedrooms_short "SZ" / feature_baths_short "Bad"
 *     — overlay abbreviations ("3 SZ · 2 Bad"); "Bad" is count-neutral by
 *     convention on listings, "Bäder" would be the plural.
 *   - explorer.price_avg_per_night "Ø/Nacht" — "Ø" is the usual German
 *     average sign; "Durchschn./Nacht" is the spelled-out alternative.
 *   - checkout.status_loading_checkout "Buchungsseite wird geladen..." —
 *     "Kasse" is the e-commerce word; a stay booking reads better as
 *     "Buchung".
 *   - checkout.label_pms_fallback_host "Ihrem Gastgeber" — dative, because
 *     both sentences that take {{pms}} read "von/bei {{pms}}".
 *   - *.feature_studio / specs.studio "Studio" and confirmation.label_row_status
 *     "Status" are the German words (listed in IDENTICAL_BY_DESIGN).
 *   - explorer.compare_* (#1816, written 2026-10-18 in lane compare-remainder,
 *     NOT native-reviewed): compare_mode_hint moves the count ahead of the
 *     button ("Tippen Sie bei bis zu %2 Unterkünften auf „+ %1“"), German
 *     quotation marks; compare_included "Vorhanden" for the amenity check mark.
 *
 * Placeholders ({{n}}, {{from}}, %1, …) are the same tokens the English
 * defaults carry; the integrity test pins them.
 */
import type { LocaleTable, WidgetStrings } from './tables'

const propertyCard: WidgetStrings<'property-card'> = {
  price_from: 'Ab',
  price_per_night: '/ Nacht',
  view_property: 'Unterkunft anzeigen',
  feature_guest: 'Gast',
  feature_guests: 'Gäste',
  feature_bedroom: 'Schlafzimmer',
  feature_bedrooms: 'Schlafzimmer',
  feature_bedrooms_short: 'SZ',
  feature_studio: 'Studio',
  feature_bath: 'Bad',
  feature_baths: 'Bäder',
  feature_baths_short: 'Bad',
  rating_reviews: 'Bewertungen',
  photo_previous: 'Vorheriges Foto',
  photo_next: 'Nächstes Foto',
  photo_position: 'Foto %1 von %2',
}

const paymentForm = {
  heading_payment_information: 'Zahlungsinformationen',
  status_loading_payment_form: 'Zahlungsformular wird geladen...',
  label_name_on_card: 'Name auf der Karte',
  placeholder_name_on_card: 'Name auf der Karte',
  label_card_number: 'Kartennummer',
  label_card_expiry: 'Gültig bis',
  placeholder_card_expiry: 'MM/JJ',
  label_card_cvc: 'CVC',
  text_powered_by: 'Bereitgestellt von',
  error_payment_processor_load_failed: 'Der Zahlungsanbieter konnte nicht geladen werden',
  error_card_form_load_failed: 'Das Kartenformular konnte nicht geladen werden. Bitte laden Sie die Seite neu und versuchen Sie es erneut.',
  error_payment_processor_not_ready: 'Der Zahlungsanbieter ist noch nicht bereit',
  error_cvc_check_failed: 'Die Prüfung der Kartenprüfnummer (CVC) ist fehlgeschlagen. Bitte überprüfen Sie Ihre Kartendaten.',
  error_payment_processing_failed: 'Die Zahlung konnte nicht verarbeitet werden',
  error_payment_setup_intent: 'Die Kartenverifizierung konnte nicht gestartet werden',
  error_guestypay_load_failed: 'GuestyPay konnte nicht geladen werden',
  error_guestypay_unavailable: 'GuestyPay-Tokenisierung nicht verfügbar',
  error_payment_form_load_failed: 'Das Zahlungsformular konnte nicht geladen werden',
  error_payment_failed: 'Zahlung fehlgeschlagen',
  error_enter_card_details: 'Bitte geben Sie Ihre Kartendaten ein',
} as const

const costGroups = {
  group_rent: 'Miete:',
  group_fees: 'Gebühr(en):',
  group_addons: 'Extra(s):',
  group_others: 'Sonstiges:',
  group_taxes: 'Steuer(n):',
} as const

const outcomeCopy = {
  inquirySent: 'Anfrage gesendet!',
  requestSent: 'Buchungsanfrage gesendet!',
  bookingConfirmed: 'Buchung bestätigt!',
  inquiryBody: 'Ihre Anfrage wurde an den Gastgeber gesendet. Sie erhalten in Kürze eine Antwort.',
  requestBody: 'Ihre Buchungsanfrage wurde übermittelt. Der Gastgeber prüft sie und bestätigt Ihre Reservierung.',
  bookingBody: 'Ihre Reservierung wurde bestätigt. Sie erhalten in Kürze eine Bestätigungs-E-Mail.',
} as const

const offers = {
  weeklyAndMonthly: 'Sparen Sie {{weekly}}% bei Wochen- und {{monthly}}% bei Monatsaufenthalten!',
  weekly: 'Sparen Sie {{weekly}}% bei Wochenaufenthalten!',
  monthly: 'Sparen Sie {{monthly}}% bei Monatsaufenthalten!',
} as const

export const de_DE: LocaleTable = {
  amenities: {
    showMoreButton: 'Mehr anzeigen',
  },

  description: {
    showMoreButton: 'Mehr anzeigen',
  },

  specs: {
    bedrooms: 'Schlafzimmer',
    bathrooms: 'Badezimmer',
    guests: 'Gäste',
    areaSize: 'Fläche',
    studio: 'Studio',
  },

  calendar: {
    error_disabled_error: 'Leider ist dieses Datum nicht verfügbar',
    error_check_in_min_stay_error: 'Abreise an diesem Tag nicht möglich: diese Unterkunft erfordert einen Mindestaufenthalt von {{value}}',
    error_select_range_between_unavailable_days_error: 'Bitte wählen Sie einen Zeitraum zwischen verfügbaren Tagen',
    error_advance_notice_error: 'Buchung nur mit {{days}} Tag(en) Vorlauf möglich',
    tip_select_stay: '{{from}} – {{to}} wählen · {{nights}}',
    tip_night_one: '{{n}} Nacht',
    tip_night_other: '{{n}} Nächte',
    header_offer_weekly_and_monthly: offers.weeklyAndMonthly,
    header_offer_weekly: offers.weekly,
    header_offer_monthly: offers.monthly,
    header_local_time: 'Ortszeit: ',
    footer_taxes_note: 'Zzgl. Steuern und Gebühren',
    footer_legend_available: 'Verfügbar für Anreise/Abreise',
    footer_legend_booked: 'Gebucht',
    footer_legend_unavailable: 'Nicht verfügbar oder eingeschränkt, z. B. durch Mindestaufenthalte',
    footer_clear_dates: 'Reisedaten löschen',
    min_stay_unit_one: 'Nacht',
    min_stay_unit_other: 'Nächte',
  },

  'property-card': propertyCard,

  explorer: {
    ...propertyCard,
    price_starting_at: 'Ab',
    price_night_one: 'Nacht',
    price_night_other: 'Nächte',
    price_total: 'gesamt',
    price_avg_per_night: 'Ø/Nacht',
    price_view_full: 'Gesamtpreis anzeigen',
    price_incl_taxes: 'inkl. Steuern und Gebühren',
    price_excl_taxes: 'zzgl. Steuern',
    price_excl_taxes_fees: 'zzgl. Steuern und Gebühren',
    grid_no_results: 'Keine Unterkünfte gefunden.',
    grid_load_more: 'Mehr laden',
    grid_loading: 'Wird geladen...',
    slider_previous: 'Zurück',
    slider_next: 'Weiter',
    slider_go_to: 'Zu Folie %1 wechseln',
    // #1816: Compare Homes chrome (written 2026-10-18, not native-reviewed).
    compare_title: 'Unterkünfte vergleichen',
    compare_clear: 'Leeren',
    compare_close: 'Schließen',
    compare_added: 'Hinzugefügt',
    compare_differences: 'Nur Unterschiede anzeigen',
    compare_copy_link: 'Link kopieren',
    compare_link_copied: 'Link kopiert.',
    compare_copy_failed: 'Kopieren Sie diesen Link: %1',
    compare_share: 'Teilen',
    compare_all_same: 'Diese Unterkünfte stimmen in allen aufgeführten Details überein.',
    compare_included: 'Vorhanden',
    compare_not_listed: 'Nicht angegeben',
    compare_price: 'Preis',
    compare_price_dates: 'Gesamtpreis für Ihre Daten',
    compare_price_from: 'Ab %1 / Nacht',
    compare_sleeps: 'Schlafplätze',
    compare_bedrooms: 'Schlafzimmer',
    compare_bathrooms: 'Badezimmer',
    compare_location: 'Lage',
    compare_amenities: 'Ausstattung',
    compare_view: 'Unterkunft anzeigen',
    compare_one_more: 'Wählen Sie 1 weitere zum Vergleichen',
    compare_mode_hint: 'Tippen Sie bei bis zu %2 Unterkünften auf „+ %1“',
    compare_of_max: '%1 von %2 ausgewählt',
    compare_max_reached: 'Sie können bis zu %1 Unterkünfte vergleichen.',
    compare_remove_home: '%1 entfernen',
    compare_missing_one: '1 verlinkte Unterkunft ist nicht in diesen Ergebnissen.',
    compare_missing_other: '%1 verlinkte Unterkünfte sind nicht in diesen Ergebnissen.',
  },

  booking: {
    notice_select_dates: 'Bitte Reisedaten wählen',
    notice_select_guests: 'Bitte Gäste wählen',
    notice_hosted_checkout_failed: 'Die Buchungsseite konnte nicht geöffnet werden. Bitte versuchen Sie es erneut.',
    notice_generic_error: 'Ein Fehler ist aufgetreten',
    notice_coupon_failed: 'Der Gutschein konnte nicht eingelöst werden',
    notice_coupon_needs_dates: 'Bitte zuerst Reisedaten und Gäste wählen',
    notice_coupon_needs_code: 'Bitte geben Sie einen Gutscheincode ein',
    notice_coupon_applied: 'Gutschein eingelöst!',
    notice_coupon_invalid: 'Gutschein für diese Reisedaten nicht gültig',
    button_processing: 'Wird verarbeitet...',
    button_check_availability: 'Verfügbarkeit prüfen',
    button_close: 'Schließen',
    button_coupon_apply: 'Einlösen',
    button_coupon_applying: 'Wird eingelöst…',
    label_check_in: 'Anreise',
    label_check_out: 'Abreise',
    label_check_in_static: 'Anreise',
    label_check_out_static: 'Abreise',
    placeholder_add_date: 'Datum hinzufügen',
    label_guests: 'Gäste',
    label_adults: 'Erwachsene',
    label_children: 'Kinder',
    label_infants: 'Babys',
    label_pets: 'Haustiere',
    hint_adults_age: 'Ab 13 Jahren',
    hint_children_age: '2 – 12 Jahre',
    hint_infants_age: 'Unter 2 Jahren',
    hint_pets_not_allowed: 'In dieser Unterkunft nicht erlaubt',
    aria_guest_selection: 'Gästeauswahl',
    aria_decrease_guests: 'Weniger Gäste',
    aria_increase_guests: 'Mehr Gäste',
    aria_decrease_adults: 'Weniger Erwachsene',
    aria_increase_adults: 'Mehr Erwachsene',
    aria_decrease_children: 'Weniger Kinder',
    aria_increase_children: 'Mehr Kinder',
    aria_decrease_infants: 'Weniger Babys',
    aria_increase_infants: 'Mehr Babys',
    aria_decrease_pets: 'Weniger Haustiere',
    aria_increase_pets: 'Mehr Haustiere',
    unit_guest_one: 'Gast',
    unit_guest_other: 'Gäste',
    unit_infant_one: 'Baby',
    unit_infant_other: 'Babys',
    unit_pet_one: 'Haustier',
    unit_pet_other: 'Haustiere',
    guest_max_note: 'Diese Unterkunft bietet Platz für maximal {{n}} {{unit}}.',
    guest_max_note_with_infants: 'Diese Unterkunft bietet Platz für maximal {{n}} {{unit}}, Babys nicht mitgezählt.',
    unit_night_one: 'Nacht',
    unit_night_other: 'Nächte',
    label_min_stay: 'Mindestaufenthalt: {{value}}',
    pill_min_stay: 'Mind. {{n}} Nächte',
    pill_min_stay_one: 'Mind. 1 Nacht',
    label_have_coupon: 'Ich habe einen Gutschein',
    placeholder_coupon_code: 'Gutscheincode',
    label_total_after_tax: 'Gesamtbetrag inkl. Steuern',
    label_total_before_taxes: 'Gesamtbetrag ohne Steuern',
    note_taxes_at_checkout: 'Steuern werden beim Bezahlen hinzugefügt',
    discount_weekly_and_monthly: offers.weeklyAndMonthly,
    discount_weekly: offers.weekly,
    discount_monthly: offers.monthly,
    dialog_title: 'Buchung',
    label_starting_at: 'Ab ',
    unit_per_night: ' / Nacht',
    unit_total: ' gesamt',
    aria_loading_price: 'Preis wird geladen',
    calendar_modal_title: 'Reisedaten wählen',
    label_when: 'Wann',
    button_clear_dates: 'Reisedaten löschen',
    label_subtotal: 'Zwischensumme:',
    label_total_modal: 'Gesamt:',
    aria_charge_explanation: 'Zu diesem Posten',
    aria_price_details_dialog: 'Preisdetails',
    aria_close_price_details: 'Preisdetails schließen',
    ...costGroups,
  },

  checkout: {
    // Checkout shell
    button_back: '← Zurück',
    status_loading_checkout: 'Buchungsseite wird geladen...',
    button_processing: 'Wird verarbeitet...',
    button_explore_properties: 'Unterkünfte entdecken',

    // Errors & notices
    notice_no_booking_selected: 'Keine Buchung ausgewählt. Bitte starten Sie auf einer Unterkunftsseite, um die Buchung abzuschließen.',
    error_checkout_load_failed: 'Die Buchungsdaten konnten nicht geladen werden.',
    error_checkout_data_unavailable: 'Die Buchungsdaten können nicht geladen werden.',
    error_load_payment_settings: 'Die Zahlungseinstellungen konnten nicht abgerufen werden',
    error_load_quote: 'Das Angebot konnte nicht abgerufen werden',
    error_load_property: 'Die Unterkunft konnte nicht abgerufen werden',
    error_reservation_failed: 'Die Reservierung konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
    error_create_reservation: 'Reservierung konnte nicht erstellt werden',
    error_hosted_checkout_failed: 'Der externe Bezahlvorgang konnte nicht gestartet werden. Bitte versuchen Sie es erneut.',
    error_hosted_checkout_url: 'Die Checkout-URL konnte nicht abgerufen werden',
    error_addon_update_failed: 'Dieses Extra konnte nicht aktualisiert werden.',
    error_update_addon: 'Extra konnte nicht aktualisiert werden',
    error_payment_session_not_resumed: 'Ihre Zahlungssitzung konnte nicht fortgesetzt werden. Bitte geben Sie Ihre Daten erneut ein, um die Buchung abzuschließen.',
    error_threeds_failed: 'Die 3D-Secure-Authentifizierung ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
    error_payment_session_wrong_booking: 'Ihre Zahlungssitzung gehörte zu einer anderen Buchung und konnte nicht fortgesetzt werden. Bitte geben Sie Ihre Zahlungsdaten erneut ein.',

    // Hosted checkout
    heading_complete_your_booking: 'Buchung abschließen',
    text_hosted_checkout_intro: 'Diese Unterkunft nutzt den sicheren Bezahlvorgang von {{pms}}. Klicken Sie unten, um weitergeleitet zu werden und Ihre Daten und die Zahlung abzuschließen.',
    label_pms_fallback_host: 'Ihrem Gastgeber',
    button_continue_to_pms_checkout: 'Weiter zum Bezahlen bei {{pms}}',
    status_redirecting: 'Weiterleitung...',

    // Guest information
    heading_guest_information: 'Gastinformationen',
    label_first_name: 'Vorname',
    placeholder_first_name: 'Vorname',
    label_last_name: 'Nachname',
    placeholder_last_name: 'Nachname',
    label_email: 'E-Mail',
    placeholder_email: 'name@beispiel.de',
    label_phone: 'Telefon',
    placeholder_phone: 'Telefonnummer',
    error_invalid_phone: 'Bitte geben Sie eine gültige Telefonnummer ein.',
    placeholder_survey_select: '— Bitte wählen —',
    placeholder_survey_other: 'Bitte angeben...',
    hint_all_fields_required: 'Alle Felder sind Pflichtfelder',
    heading_mailing_address: 'Postanschrift',
    label_address_line_1: 'Adresszeile 1',
    placeholder_address_line_1: 'Straße und Hausnummer',
    label_address_line_2: 'Adresszeile 2',
    placeholder_address_line_2: 'Wohnung, Etage usw.',
    label_city: 'Stadt',
    label_state: 'Bundesland',
    placeholder_state: 'Bundesland / Region',
    label_zip_code: 'Postleitzahl',
    placeholder_zip_code: 'PLZ',
    label_country: 'Land',
    placeholder_select_country: 'Land auswählen',

    // Agreements & consent
    heading_rental_agreement: 'Mietvertrag',
    text_acceptance_privacy_and_terms: 'Ich habe die oben stehenden Hausregeln und Stornierungsbedingungen sowie die {{privacy}} und die {{terms}} gelesen und akzeptiere sie.',
    text_acceptance_terms_only: 'Ich habe die oben stehenden Hausregeln und Stornierungsbedingungen sowie die {{terms}} gelesen und akzeptiere sie.',
    text_acceptance_privacy_only: 'Ich habe die oben stehenden Hausregeln und Stornierungsbedingungen sowie die {{privacy}} gelesen und akzeptiere sie.',
    text_acceptance_no_links: 'Ich habe die oben stehenden Hausregeln und Stornierungsbedingungen gelesen und akzeptiere sie.',
    label_privacy_policy: 'Datenschutzerklärung',
    label_terms: 'Nutzungsbedingungen',
    label_signature_draw: 'Unten unterschreiben',
    label_signature_name: 'Unterschreiben Sie, indem Sie Ihren vollständigen Namen eingeben',
    label_signature_initials: 'Geben Sie Ihre Initialen ein, um zu unterschreiben',
    button_clear_signature: 'Unterschrift löschen',
    aria_close_consent_dialog: 'Schließen',

    // Payment form
    ...paymentForm,

    // Booking summary
    heading_booking_summary: 'Buchungsübersicht',
    label_check_in: 'Anreise',
    label_check_out: 'Abreise',
    label_guests: 'Gäste',
    label_nights: 'Nächte',
    label_total: 'Gesamt',
    ...costGroups,
    label_taxes: 'Steuern',

    // Property & policies
    heading_house_rules: 'Hausregeln',
    heading_cancellation_policy: 'Stornierungsbedingungen',
    heading_payment_schedule: 'Zahlungsplan',
    text_payment_term_at_booking: 'bei Buchung',
    label_payment_term_before: 'vor',
    label_payment_term_after: 'nach',
    text_payment_term_timing_relative: '{{count}} {{unit}} {{relation}} {{event}}',
    text_payment_term_line: '{{amount}} {{timing}}',

    // Confirmation screen
    heading_inquiry_sent: outcomeCopy.inquirySent,
    heading_request_sent: outcomeCopy.requestSent,
    heading_booking_confirmed: outcomeCopy.bookingConfirmed,
    text_inquiry_sent_body: outcomeCopy.inquiryBody,
    text_request_sent_body: outcomeCopy.requestBody,
    text_booking_confirmed_body: outcomeCopy.bookingBody,
    label_reservation_id: 'Reservierungsnummer:',
    notice_payment_required: 'Für Ihre Reservierung ist eine Zahlung erforderlich. Bitte schließen Sie die Zahlung ab, um Ihre Buchung zu bestätigen.',
    button_complete_payment: 'Zahlung abschließen',
    button_go_back: 'Zurück',
  },

  confirmation: {
    // Page states
    status_loading_reservation: 'Reservierungsdetails werden geladen...',
    error_invalid_link: 'Leider ist der aufgerufene Link ungültig oder abgelaufen.',
    error_reservation_unavailable: 'Die Reservierungsdaten können nicht geladen werden.',
    error_fetch_reservation_failed: 'Die Reservierung konnte nicht abgerufen werden',

    // Success modal
    heading_modal_inquiry_sent: outcomeCopy.inquirySent,
    heading_modal_request_sent: outcomeCopy.requestSent,
    heading_modal_booking_confirmed: outcomeCopy.bookingConfirmed,
    text_modal_message_inquiry: outcomeCopy.inquiryBody,
    text_modal_message_request: outcomeCopy.requestBody,
    text_modal_message_booking: outcomeCopy.bookingBody,
    aria_modal_close: 'Schließen',
    button_modal_got_it: 'Verstanden!',

    // Reservation summary
    label_row_type: 'Art',
    label_row_status: 'Status',
    label_row_checkin: 'Anreise',
    label_row_checkout: 'Abreise',
    label_row_guests: 'Gäste',
    label_type_inquiry: 'Anfrage',
    label_type_booking: 'Buchung',
    label_type_request: 'Buchungsanfrage',
    label_status_received: 'Eingegangen',
    label_status_pending: 'Ausstehend',
    label_status_confirmed: 'Bestätigt',
    label_status_booked: 'Gebucht',
    label_status_requested: 'Angefragt',
    label_status_closed: 'Geschlossen',

    // Cost breakdown
    heading_cost_breakdown: 'Kostenaufstellung',
    label_total: 'Gesamt',
    label_taxes: 'Steuern',
    ...costGroups,

    // Payment recovery
    notice_payment_method_failed: 'Ihre vorherige Zahlungsmethode ist fehlgeschlagen. Bitte geben Sie neue Zahlungsdaten an.',
    button_pay_now: 'Jetzt bezahlen',
    status_completing_payment: 'Ihre Zahlung wird abgeschlossen…',
    button_submitting: 'Wird gesendet…',
    button_submit_payment: 'Zahlung absenden',
    text_payment_recovery_success: 'Vielen Dank, Ihre Zahlungsdaten wurden erfolgreich übermittelt.',
    error_payment_submit_failed: 'Die Zahlung konnte nicht übermittelt werden. Bitte versuchen Sie es erneut.',
    error_threeds_auth_failed: 'Die 3D-Secure-Authentifizierung ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
    error_threeds_resume_failed: 'Ihre Zahlungssitzung konnte nicht fortgesetzt werden. Bitte geben Sie Ihre Zahlungsdaten erneut ein.',
    error_threeds_recovery_unavailable: 'Ihre Karte wurde authentifiziert, aber die Zahlung konnte auf dieser Seite nicht abgeschlossen werden. Bitte kontaktieren Sie uns, um die Zahlung abzuschließen.',

    // Payment form (shared with Checkout)
    ...paymentForm,
  },
}
